/**
 * KategorieWorkspace — Platforma → Půjčovna: the platform-wide web categories of the rental module
 * (list, create via the full-page `KategorieForm`, edit via `KategorieEditModal`, delete). Platform
 * admins only, like the API. PlatformaPage mounts this before membership has resolved, so the admin
 * flag is passed in (as the other Platforma workspaces do): until it is true nothing is queried and
 * no privileged control is rendered.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useDeleteKategorieMutation, useKategorieQuery, type PujcovnaKategorie } from './api';
import { KategorieEditModal } from './KategorieEditModal';

interface KategorieWorkspaceProps {
  isAdmin: boolean;
}

export function KategorieWorkspace({ isAdmin }: KategorieWorkspaceProps) {
  const navigate = useNavigate();
  const kategorie = useKategorieQuery(isAdmin);
  const del = useDeleteKategorieMutation();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<PujcovnaKategorie | null>(null);

  const remove = async (k: PujcovnaKategorie) => {
    const ok = await confirm({
      title: (
        <>
          Smazat kategorii <em>{k.nazev}</em>?
        </>
      ),
      description: 'Kategorii nelze smazat, dokud ji používají vozidla.',
      tone: 'danger',
      confirmLabel: 'Smazat',
    });
    if (!ok) return;
    del.mutate(k.id, {
      onSuccess: () => toast.ok('Kategorie smazána.'),
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se smazat kategorii.'}`),
    });
  };

  if (!isAdmin) return <div className="px-8 pt-4" aria-hidden />;

  const rows = kategorie.data ?? [];

  const columns: DataTableColumn<PujcovnaKategorie>[] = [
    {
      id: 'nazev',
      header: 'Kategorie',
      width: 'minmax(180px, 1.4fr)',
      priority: 'primary',
      cell: (k) => <span className="text-[14px] text-ink">{k.nazev}</span>,
    },
    {
      id: 'ikona',
      header: 'Ikona',
      width: 'minmax(120px, 1fr)',
      priority: 'secondary',
      cell: (k) => <span className="font-mono text-[12px] text-ink-3">{k.ikona || '—'}</span>,
    },
    {
      id: 'slug',
      header: 'Odkaz na webu',
      width: 'minmax(140px, 1fr)',
      priority: 'secondary',
      cell: (k) =>
        k.slug ? (
          <span className="font-mono text-[12px] text-ink-3">{k.slug}</span>
        ) : (
          <Badge tone="warn" size="sm">
            Bez odkazu
          </Badge>
        ),
    },
    {
      id: 'akce',
      header: '',
      width: '180px',
      align: 'right',
      priority: 'meta',
      cell: (k) => (
        <span className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditing(k)}>
            Upravit
          </Button>
          <Button variant="secondary" size="sm" onClick={() => remove(k)} disabled={del.isPending}>
            Smazat
          </Button>
        </span>
      ),
    },
  ];

  // Same shape as the other Platforma tabs: a toolband (count + the one primary action), then the
  // gray body with a bordered table panel on it.
  return (
    <div data-testid="platforma-pujcovna" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <Toolband.Spacer />
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {kategorie.isLoading ? '—' : `${rows.length} kategorií`}
        </span>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="plus" size={12} />}
          onClick={() => navigate({ to: '/platforma/pujcovna/kategorie/nova' })}
        >
          Nová kategorie
        </Button>
      </Toolband>

      <div data-testid="kategorie-list" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
        {kategorie.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : kategorie.isError ? (
          <EmptyState eyebrow="Chyba" title="Kategorie se nepodařilo načíst" />
        ) : rows.length === 0 ? (
          <EmptyState
            eyebrow="Bez kategorií"
            title="Zatím tu není žádná kategorie"
            description="Kategorie jsou společné pro všechny firmy. Založte první tlačítkem „Nová kategorie“ vpravo nahoře."
          />
        ) : (
          <DataTable<PujcovnaKategorie>
            aria-label="Kategorie vozidel"
            variant="bordered"
            columns={columns}
            data={rows}
            getRowId={(k) => String(k.id)}
          />
        )}
      </div>

      {editing && <KategorieEditModal kategorie={editing} open onOpenChange={(open) => !open && setEditing(null)} />}
      {dialog}
    </div>
  );
}
